'use client';

import { useMemo, useCallback } from 'react';
import { useRouter, useSearchParams, usePathname } from 'next/navigation';

//* HOC's
import { withLanguageContext, withUIContext } from '@/context';

//* Helpers
import { createQueryString } from '@/helpers/_functions';

//* Components
import Page from '@/components/common/Page';
import Text from '@/components/common/Text';
import ShopItem from '@/components/common/ShopItem';
import TopBar from '@/components/pages/shop/TopBar';
import Container from '@/components/common/Container';
import LeftBar from '@/components/pages/shop/LeftBar';
import Pagination from '@/components/common/Pagination';
import MobileFilter from '@/components/pages/shop/MobileFilter';
import CategoryDescriptionSection from '@/components/sections/CategoryDescriptionSection';

//* Style
import ShopStyle from '@/styles/pages/ShopStyle';

const CatalogTemplate = ({ breadcrumbs, filters, products, total, pageSize, winWidth, selectedCat, translate }) => {
	//! Router
	const searchParams = useSearchParams();
	const pathname = usePathname();
	const router = useRouter();

	//! On Filter Param Change
	const onFilterChange = useCallback(
		(type, typeId, multiple = false) => {
			const params = createQueryString({ type, typeId, multiple, searchParams });

			router.push(pathname + '?' + params);
		},
		[searchParams]
	);

	//! Items
	const storeListItems = useMemo(() => {
		return products?.length > 0
			? products.map((item, index) => {
				return (
					<div
						key={item?.id ?? item?.slug ?? `product-${index}`}
						className={`product-col`}
					>
						<ShopItem {...item} />
					</div>
				);
			})
			: null;
	}, [products]);

	return (
		<Page className='shop'>
			<ShopStyle>
				<Container section>
					{winWidth >= 1024 ? (
						<TopBar
							hasSortBy
							breadcrumbs={breadcrumbs}
							onFilterChange={onFilterChange}
						/>
					) : null}

					{!(winWidth >= 1024) ? (
						<MobileFilter
							filters={filters}
							searchParams={searchParams}
							onFilterChange={onFilterChange}
						/>
					) : null}

					<div className='main-content-wrap'>
						{winWidth >= 1024 ? (
							<LeftBar
								filters={filters}
								onFilterChange={onFilterChange}
								searchParams={searchParams}
							/>
						) : null}

						<div className='items-listing'>
							{products?.length > 0 && searchParams.get('s') ? (
								<>
									<Text className={`h2 font-futura-regular product-empty-text`}>
										{translate('youSearchedFor')}
										&nbsp;
										<span className='h2-L font-futura-semibold'>{`“${searchParams.get(
											's'
										)}”`}</span>
									</Text>

									{storeListItems}
								</>
							) : products?.length > 0 && !searchParams.get('s') ? (
								storeListItems
							) : !(products?.length > 0) && searchParams.get('s') ? (
								<Text className={`h2 font-futura-regular product-empty-text`}>
									{translate('oopsNoResults')}
									&nbsp;
									<span className='h2-L font-futura-semibold'>{`“${searchParams.get('s')}”`}</span>
								</Text>
							) : (
								<Text
									text={'noProductsFound'}
									className={'h2 font-futura-regular product-empty-text'}
								/>
							)}
						</div>
					</div>

					<Pagination
						total={total}
						pageSize={pageSize}
						onCategoryChange={onFilterChange}
					/>
				</Container>
			</ShopStyle>

			{selectedCat?.description ? <CategoryDescriptionSection {...selectedCat} /> : null}
		</Page>
	);
};

export default withLanguageContext(withUIContext(CatalogTemplate, ['winWidth']), ['translate']);
